"use client";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import crownWordmark from "../resources/CrownCrackedWithTYK.png";
import {
  ArrowRight,
  BookOpen,
  Briefcase,
  ChevronLeft,
  ChevronRight,
  HandCoins,
  Megaphone,
  Network,
  ShieldCheck,
  ShoppingBasket,
  Store,
  Users,
  Vote as VoteIcon,
  Wheat,
  Wrench,
} from "lucide-react";
import GlobeWithPins from "./GlobeWithPins";
import FishersOfMen from "../resources/Fisherman.jpg";
import wheatfieldVideo from "../resources/wheatfield.mp4";
import ColoniesSection from './ColoniesSection';
import DailyVerse from "./DailyVerse";
import { useTranslation } from "../i18n";
import CommunityForumV2 from './Forum/CommunityForumV2'
import { FEATURE_FLAGS, isFeatureEnabled } from '../config/featureFlags';

export default function HeroSection() {
  const { t } = useTranslation();
  const marketplaceEnabled = isFeatureEnabled(FEATURE_FLAGS.marketplaceListings);
  const jobsEnabled = isFeatureEnabled(FEATURE_FLAGS.jobsPayroll);
  const servicesEnabled = isFeatureEnabled(FEATURE_FLAGS.services);
  const [activeMissionSlide, setActiveMissionSlide] = useState(0);
  const primaryTools = [
    { label: "Learn", path: "/learn", icon: BookOpen },
    { label: "Media", path: "/media", icon: Megaphone },
    { label: "Needs", path: "/needs", icon: HandCoins },
    { label: "Create Group", path: "/post-group", icon: Users },
    { label: "Explore Communities", path: "/fetch-groups", icon: Network },
    ...(marketplaceEnabled ? [{ label: "Marketplace", path: "/marketplace", icon: ShoppingBasket }] : []),
    ...(jobsEnabled ? [{ label: "Jobs", path: "/jobs", icon: Briefcase }] : []),
    ...(servicesEnabled ? [{ label: "Services", path: "/services", icon: Wrench }] : []),
  ];

  const missionCarouselSlides = [
    {
      title: "Unite the Body of Christ",
      caption: "Unite the Body of Christ to advance the Kingdom of Heaven across the world. It starts with churches communing with one another, sharing strength across congregations and denominations, and building a Kingdom movement that grows from neighborhood fellowship to global communion.",
    },
    {
      title: "Allow Christ to Work Through Us",
      caption: "Just say yes to God. If you are called to do something, act on it. Post your idea so that other people in the Body of Christ can help bring it to life.",
    },
    {
      title: "Advance the Kingdom",
      caption: "A global, Christ-centered, Kingdom-minded movement designed to unite believers across the world so the Body of Christ can work together to do His will and make the world better reflect the Kingdom of Heaven.",
    },
    {
      title: "Bring Them In",
      caption: "Spread the Gospel across the Earth and help every person encounter Christ, discover their true purpose and identity, and find a place to belong.",
    },
  ];

  const activeSlide = missionCarouselSlides[activeMissionSlide];
  const showPreviousMissionSlide = () => {
    setActiveMissionSlide((current) => (current + missionCarouselSlides.length - 1) % missionCarouselSlides.length);
  };
  const showNextMissionSlide = () => {
    setActiveMissionSlide((current) => (current + 1) % missionCarouselSlides.length);
  };

  const missionLanes = [
    {
      kicker: "Unite",
      title: "Unite the Body",
      body: "Groups, congregations, businesses, ministries, and local believers become discoverable as part of one connected Body.",
      icon: Network,
    },
    {
      kicker: "Organize",
      title: "Decide with transparency",
      body: "Ideas, shared discernment, transparent decisions, stewardship, and coordinated action help communities move together at scale.",
      icon: VoteIcon,
    },
    {
      kicker: "Change",
      title: "Reflect Heaven on earth",
      body: "The aim is not merely to win arguments, but to live 'Thy Kingdom come, thy will be done, on earth as it is in Heaven.'",
      icon: Wheat,
    },
    {
      kicker: "Abundance",
      title: "Build Kingdom Abundance",
      body: "Share generously, meet real needs, practice honest business, and exchange goods and services in ways that reflect the character of Christ.",
      icon: ShoppingBasket,
    },
    {
      kicker: "Restore",
      title: "Return to Yeshua's Original Design",
      body: "Recover the shared life of the early Church: believers drawing close to God, breaking bread, praying, worshiping, sharing what they have, and serving together as one Body.",
      icon: Users,
    },
  ];

  const featureCards = [
    {
      title: "Connect Your Church",
      desc: "Bring your church, ministry, or congregation into coordination with the wider Body of Christ across TYK.",
      icon: Users,
      path: "/post-group",
    },
    {
      title: "Start a Kingdom Circle",
      desc: "Create a purpose-driven circle for prayer, ideas, voting, work, trade, service, and action.",
      icon: Network,
      path: "/post-group",
    },
    ...(jobsEnabled ? [{
      title: "Jobs & Roles",
      desc: "Find or offer work, define trusted responsibilities, and help people get paid for service that actually matters.",
      icon: Briefcase,
      path: "/jobs",
    }] : []),
    ...(marketplaceEnabled ? [{
      title: "Buy/Sell/Barter",
      desc: "Trade goods and services with purpose through a Kingdom economy built around trust, reputation, and stewardship.",
      icon: Store,
      path: "/marketplace",
    }] : []),
    ...(servicesEnabled ? [{
      title: "Services",
      desc: "Offer or hire essential services, from practical help to skilled work, with visible signals of trust.",
      icon: Wrench,
      path: "/services",
    }] : []),
  ];

  return (
    <main className="home-overhaul">
      <section className="home-hero" style={{ backgroundImage: `url(${FishersOfMen})` }}>
        <div className="home-hero-shade" aria-hidden="true" />
        <div className="home-hero-grain" aria-hidden="true" />
        <DailyVerse />
        <nav className="home-quick-menu home-top-tool-menu" aria-label={t('hero.quickMenu.ariaLabel')}>
          {primaryTools.map(({ label, icon: Icon, path, anchor }) => {
            const menuContent = (
              <>
                <Icon size={18} aria-hidden="true" />
                <span>{label}</span>
              </>
            );

            return anchor ? (
              <a key={label} href={path} className="home-quick-menu-item">
                {menuContent}
              </a>
            ) : (
              <Link key={label} to={path} className="home-quick-menu-item">
                {menuContent}
              </Link>
            );
          })}
        </nav>
        <section className="home-mission-reel home-mission-reel-auth" aria-label="Mission carousel">
          <video
            src={wheatfieldVideo}
            aria-hidden="true"
            className="home-mission-reel-image"
            autoPlay
            muted
            loop
            playsInline
          />
          <div className="home-mission-reel-scrim" aria-hidden="true" />
          <img src={crownWordmark} alt={t('hero.title')} className="home-mission-reel-logo" />
          <div className="home-mission-reel-content">
            <span className="home-kicker home-mission-reel-kicker">
              <span><Wheat size={16} /> Kingdom movement</span>
              <span className="home-mission-reel-count">{activeMissionSlide + 1} of {missionCarouselSlides.length}</span>
            </span>
            <h2>{activeSlide.title}</h2>
            <p>{activeSlide.caption}</p>
          </div>
          <button
            type="button"
            className="home-mission-reel-control home-mission-reel-control-prev"
            onClick={showPreviousMissionSlide}
            aria-label="Previous mission slide"
          >
            <ChevronLeft size={28} />
          </button>
          <button
            type="button"
            className="home-mission-reel-control home-mission-reel-control-next"
            onClick={showNextMissionSlide}
            aria-label="Next mission slide"
          >
            <ChevronRight size={28} />
          </button>
          <div className="home-mission-reel-dots pl-mission-reel-dots" role="tablist" aria-label="Mission slides">
            {missionCarouselSlides.map((slide, index) => (
              <button
                key={slide.title}
                type="button"
                className={index === activeMissionSlide ? "is-active" : ""}
                onClick={() => setActiveMissionSlide(index)}
                aria-label={`Show ${slide.title}`}
                aria-selected={index === activeMissionSlide}
              >
                <span>{index + 1}</span>
              </button>
            ))}
          </div>
        </section>
      </section>

      <section className="home-section home-structure-section" aria-labelledby="home-structure-heading">
        <div className="home-section-header">
          <span className="home-kicker"><ShieldCheck size={16} /> Mission into structure</span>
          <h2 id="home-structure-heading">One Mission</h2>
          <p>
            Unite the Body of Christ across the world to advance the Kingdom of Heaven.
          </p>
        </div>
        <div className="home-mission-lanes">
          {missionLanes.map(({ kicker, title, body, icon: Icon }, index) => (
            <article key={title} className="home-mission-lane">
              <strong className="home-lane-number">{String(index + 1).padStart(2, "0")}</strong>
              <div className="home-card-icon"><Icon size={26} /></div>
              <span>{kicker}</span>
              <h3>{title}</h3>
              <p>{body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="home-section home-feature-section" aria-labelledby="home-feature-heading">
        <div className="home-section-header">
          <span className="home-kicker"><HandCoins size={16} /> Shared work, shared provision</span>
          <h2 id="home-feature-heading">Christ Centered Kingdom Minded</h2>
          <p>
            ThousandYearKingdom brings coordination, work, trade, and service into one place so people can contribute where they are actually needed.
          </p>
        </div>
        <div className="home-feature-grid">
          {featureCards.map(({ title, icon: Icon, desc, path }) => (
            <article key={title} className="home-feature-card">
              <div className="home-card-icon"><Icon size={28} /></div>
              <h3>{title}</h3>
              <p>{desc}</p>
              <Link to={path} className="home-card-link">
                Open tool <ArrowRight size={16} />
              </Link>
            </article>
          ))}
        </div>
      </section>

      <section className="home-section home-globe-section" aria-labelledby="home-globe-heading">
        <div className="home-globe-header">
          <div>
            <span className="home-kicker"><Wheat size={16} /> {t('hero.fieldKicker')}</span>
            <h2 id="home-globe-heading">{t('hero.gamifyHeading')}</h2>
            <p>
              {t('hero.gamifyBody')}
            </p>
          </div>
          <div className="home-globe-stat">
            <strong>6</strong>
            <span>{t('hero.interactiveLayers')}</span>
          </div>
        </div>

        <div className="home-scripture-callout">
          <p>
            {t('hero.genesisLight')}
          </p>
        </div>

        <div className="home-globe-theatre">
          <GlobeWithPins darkMode heightClass="h-[520px] md:h-[700px] lg:h-[760px]" />
        </div>
      </section>

      <section className="home-section home-community-section" aria-label="Community conversations and groups">
        <CommunityForumV2 />
        <ColoniesSection />
      </section>
    </main>
  );
}
